import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import * as Plotly from 'plotly.js-dist';
import { Camera } from '@/models/camera';
import { format } from 'date-fns';
import { State } from 'vuex-class';
import { TelemetryItemDto } from '@/services/cameras/telemetry.interfaces';

type SolarPoint = {
  date: Date;
  powerW: number | null;
  currentA: number | null;
  voltageV: number | null;
  charging: boolean | null;
  valid: boolean | null;
};

type LevelPoint = { date: Date; level: number };
type EtaMode = 'DISCHARGE' | 'CHARGE_OR_STABLE' | 'INSUFFICIENT';

type EtaResult = {
  mode: EtaMode;
  etaHours: number | null;
  slopePctPerHour: number | null; // pente sur enveloppe basse
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
};

type CloudClass = 'CLEAR' | 'MIXED' | 'CLOUDY' | 'OVERCAST' | 'NO_DATA';

type CloudinessResult = {
  cls: CloudClass;
  score: number | null;
  p50: number | null;
  p95: number | null;
  cv: number | null;
  spike: number | null;
};

type DailySolarKpi2 = {
  dayKey: string;
  yMidNorm: number | null;
  cloud: CloudinessResult;
  peakAzimuth: number | null;
};

@Component
export default class BatterieLevelComponent extends Vue {
  @Prop({ type: Object, required: true }) public readonly camera!: Camera;

  // ✅ TELEMETRY (nouvelle source) injectée depuis le parent
  @Prop({ type: Array, required: false, default: () => [] })
  public readonly telemetry!: TelemetryItemDto[];

  @State('locale', { namespace: 'profile' }) public locale!: string;

  private syncingX = false;
  private sharedXRange: [any, any] | null = null;
  private syncingXTimeout: number | null = null;

  private readonly ETA_THRESHOLD = 5; // %
  private readonly ETA_BUCKET_HOURS = 1; // bucket minima
  private readonly SLOPE_EPS = 0.05; // %/h stable si pente >= -0.05
  private readonly ETA_POINTS = 200; // derniers N points
  private readonly ETA_MIN_SPAN_HOURS = 2; // au moins 2h
  private readonly ETA_MIN_POINTS = 30; // au moins 30 points

  private readonly DEFAULT_RANGE_DAYS = 7;

  private getDefaultRange(): [Date, Date] {
    const end = new Date();
    const start = new Date(end.getTime() - this.DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000);
    return [start, end];
  }

  // ----------------------------------------------------
  // Utilitaires
  // ----------------------------------------------------

  private lastN(points: LevelPoint[], n: number): LevelPoint[] {
    const sorted = [...points].sort((a, b) => a.date.getTime() - b.date.getTime());
    if (sorted.length <= n) return sorted;
    return sorted.slice(sorted.length - n);
  }

  private safeDate(raw: any): Date | null {
    const d = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private ddbUnwrap(v: any): any {
    // DynamoDB AttributeValue wrappers
    if (v && typeof v === 'object') {
      if ('N' in v) return Number(v.N);
      if ('S' in v) return String(v.S);
      if ('BOOL' in v) return Boolean(v.BOOL);
      if ('M' in v) return v.M;
      if ('L' in v) return v.L;
    }
    return v;
  }

  private ddbNumber(v: any): number | null {
    const u = this.ddbUnwrap(v);
    const n = Number(u);
    return Number.isFinite(n) ? n : null;
  }

  private ddbDate(v: any): Date | null {
    const u = this.ddbUnwrap(v);
    if (u instanceof Date) return u;

    const n = typeof u === 'number' ? u : Number(u);
    if (Number.isFinite(n)) {
      const ms = n < 1e12 ? n * 1000 : n;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(u);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private normalizeLevelItem(it: any): { date: Date; level: number } | null {
    // accepte item brut OU {M:{...}}
    const m = it && it.M ? it.M : it;

    const d = this.ddbDate(m?.date);
    const lvl = this.ddbNumber(m?.level ?? m?.value ?? m?.percentage ?? m?.battery);

    if (!d || lvl == null) return null;
    return { date: d, level: lvl };
  }

  private median(values: number[]): number | null {
    const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
    if (!v.length) return null;
    const mid = Math.floor(v.length / 2);
    return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
  }

  public get dateFormat(): string {
    return this.locale === 'en' ? 'MM/dd' : 'dd/MM';
  }

  // ----------------------------------------------------
  // ✅ TELEMETRY -> points batterie interne/externe
  // ----------------------------------------------------

  private get telemetryTempPoints(): Array<{ date: Date; temp: number }> {
    const pts = (this.telemetry ?? [])
      .filter((x) => Number.isFinite((x as any)?.tempC))
      .map((x) => ({ date: new Date((x as any).ts * 1000), temp: Number((x as any).tempC) }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    return pts;
  }

  private get telemetryPointsInternal(): LevelPoint[] {
    const pts = (this.telemetry ?? [])
      .filter((x) => Number.isFinite((x as any)?.batteryPercent))
      .map((x) => ({ date: new Date((x as any).ts * 1000), level: Number((x as any).batteryPercent) }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    return pts;
  }

  private get telemetryPointsExternal(): LevelPoint[] {
    const pts = (this.telemetry ?? [])
      .filter((x) => Number.isFinite((x as any)?.externalBatteryPercent))
      .map((x) => ({ date: new Date((x as any).ts * 1000), level: Number((x as any).externalBatteryPercent) }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    return pts;
  }

  // ----------------------------------------------------
  // Points internes / externes (telemetry prioritaire)
  // ----------------------------------------------------

  private get internalBatteryPoints(): LevelPoint[] {
    const tel = this.telemetryPointsInternal;
    if (tel.length) return tel;

    // fallback legacy
    const raw = (this.camera as any)?.batteryLevels ?? [];
    const pts: LevelPoint[] = [];
    for (const it of raw as any[]) {
      const p = this.normalizeLevelItem(it);
      if (p) pts.push(p);
    }
    return pts.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  private get internalBatteryLastLevel(): number | null {
    const pts = this.internalBatteryPoints;
    if (!pts.length) return null;
    return pts[pts.length - 1].level;
  }

  private get externalBatteryPoints(): LevelPoint[] {
    const tel = this.telemetryPointsExternal;
    if (tel.length) return tel;

    // fallback legacy
    const raw = (this.camera as any)?.externalBatteryLevels ?? [];
    const pts: LevelPoint[] = [];
    for (const it of raw as any[]) {
      const p = this.normalizeLevelItem(it);
      if (p) pts.push(p);
    }
    return pts.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  private get externalBatteryLastLevel(): number | null {
    const pts = this.externalBatteryPoints;
    if (!pts.length) return null;
    return pts[pts.length - 1].level;
  }

  private get hasExternalBattery(): boolean {
    return this.externalBatteryPoints.length > 0;
  }

  // ----------------------------------------------------
  // ETA / Trend
  // ----------------------------------------------------

  private bucketMinima(points: LevelPoint[], bucketHours = 2): LevelPoint[] {
    if (points.length < 10) return [];
    const sorted = [...points].sort((a, b) => a.date.getTime() - b.date.getTime());

    const t0 = sorted[0].date.getTime();
    const buckets = new Map<number, LevelPoint>();

    for (const p of sorted) {
      const idx = Math.floor((p.date.getTime() - t0) / (bucketHours * 3600000));
      const existing = buckets.get(idx);
      if (!existing || p.level < existing.level) buckets.set(idx, p);
    }

    return [...buckets.entries()].sort((a, b) => a[0] - b[0]).map(([, p]) => p);
  }

  private linearRegressionSlopePctPerHour(points: LevelPoint[]): number | null {
    if (points.length < 5) return null;

    const xs = points.map((p) => p.date.getTime() / 3600000); // heures
    const ys = points.map((p) => p.level);

    const n = xs.length;
    const meanX = xs.reduce((a, b) => a + b, 0) / n;
    const meanY = ys.reduce((a, b) => a + b, 0) / n;

    let num = 0;
    let den = 0;
    for (let i = 0; i < n; i++) {
      const dx = xs[i] - meanX;
      num += dx * (ys[i] - meanY);
      den += dx * dx;
    }
    if (den === 0) return null;
    return num / den;
  }

  private estimateEta(pointsRaw: LevelPoint[], lastLevel: number): EtaResult {
    const window = this.lastN(pointsRaw, this.ETA_POINTS);

    const spanHours = window.length
      ? (window[window.length - 1].date.getTime() - window[0].date.getTime()) / 3600000
      : 0;

    if (window.length < this.ETA_MIN_POINTS || spanHours < this.ETA_MIN_SPAN_HOURS) {
      return { mode: 'INSUFFICIENT', etaHours: null, slopePctPerHour: null, confidence: 'LOW' };
    }

    const mins = this.bucketMinima(window, this.ETA_BUCKET_HOURS);
    const slope = this.linearRegressionSlopePctPerHour(mins);

    const confidence: EtaResult['confidence'] =
      spanHours >= 24 && mins.length >= 12 ? 'HIGH' : spanHours >= 8 && mins.length >= 6 ? 'MEDIUM' : 'LOW';

    if (slope == null) {
      return { mode: 'INSUFFICIENT', etaHours: null, slopePctPerHour: null, confidence };
    }

    if (slope >= -this.SLOPE_EPS) {
      return { mode: 'CHARGE_OR_STABLE', etaHours: null, slopePctPerHour: slope, confidence };
    }

    const remaining = lastLevel - this.ETA_THRESHOLD;
    if (remaining <= 0) return { mode: 'DISCHARGE', etaHours: 0, slopePctPerHour: slope, confidence };

    const etaHours = remaining / -slope;
    return { mode: 'DISCHARGE', etaHours, slopePctPerHour: slope, confidence };
  }

  private formatEta(etaHours: number | null): string {
    if (etaHours == null || !Number.isFinite(etaHours)) return '—';
    if (etaHours <= 0) return '0h';
    if (etaHours > 24 * 30) return '>30j';

    const h = Math.floor(etaHours);
    const m = Math.round((etaHours - h) * 60);
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m} min`;
  }

  private etaUi(res: EtaResult): { label: string; sub: string | null; ui: { colorClass: string } } {
    if (res.mode === 'INSUFFICIENT') {
      return { label: '—', sub: 'Données insuffisantes', ui: { colorClass: 'text-muted' } };
    }

    const slope = res.slopePctPerHour;
    const slopeTxt = slope == null ? '' : `${slope.toFixed(2)} %/h`;
    const confTxt =
      res.confidence === 'HIGH'
        ? 'confiance élevée'
        : res.confidence === 'MEDIUM'
        ? 'confiance moyenne'
        : 'confiance faible';

    if (res.mode === 'CHARGE_OR_STABLE') {
      return {
        label: 'Charge nette / stable',
        sub: `${slopeTxt} · ${confTxt}`,
        ui: { colorClass: 'text-batt-good' },
      };
    }

    const etaLabel = this.formatEta(res.etaHours);
    const colorClass =
      res.etaHours != null && res.etaHours < 24
        ? 'text-danger'
        : res.etaHours != null && res.etaHours < 72
        ? 'text-warning'
        : 'text-primary';

    return {
      label: etaLabel,
      sub: `${slopeTxt} · ${confTxt} · seuil ${this.ETA_THRESHOLD}%`,
      ui: { colorClass },
    };
  }

  private get batteryEtaInternal() {
    const last = this.internalBatteryLastLevel;
    if (last == null)
      return this.etaUi({ mode: 'INSUFFICIENT', etaHours: null, slopePctPerHour: null, confidence: 'LOW' });
    return this.etaUi(this.estimateEta(this.internalBatteryPoints, last));
  }

  private get batteryEtaExternal() {
    const last = this.externalBatteryLastLevel;
    if (last == null)
      return this.etaUi({ mode: 'INSUFFICIENT', etaHours: null, slopePctPerHour: null, confidence: 'LOW' });
    return this.etaUi(this.estimateEta(this.externalBatteryPoints, last));
  }

  private get trendSummary(): string {
    const pts = this.hasExternalBattery ? this.externalBatteryPoints : this.internalBatteryPoints;
    const last = this.hasExternalBattery ? this.externalBatteryLastLevel : this.internalBatteryLastLevel;
    if (!pts.length || last == null) return '—';

    const res = this.estimateEta(pts, last);
    if (res.slopePctPerHour == null) return '—';

    const perDay = res.slopePctPerHour * 24;
    const sign = perDay > 0 ? '+' : '';
    return `${sign}${perDay.toFixed(1)} %/jour`;
  }

  // ----------------------------------------------------
  // ✅ SOLAR: normalisation telemetry (prio) + fallback legacy
  // ----------------------------------------------------

  private get solarLevelsNormalized(): SolarPoint[] {
    // A) TELEMETRY
    const tel = (this.telemetry ?? []) as any[];
    const outTel: SolarPoint[] = [];

    for (const it of tel) {
      if (!it) continue;

      const ts = Number(it.ts);
      if (!Number.isFinite(ts)) continue;

      const valid =
        it.solarValid === true || it.solarValid === '1' || it.solarValid === 1
          ? true
          : it.solarValid === false || it.solarValid === '0' || it.solarValid === 0
          ? false
          : null;

      if (valid === false) continue;

      const d = new Date(ts * 1000);

      const pMw = Number(it.solarPowerMw);
      const iMa = Number(it.solarCurrentMa);
      const vMv = Number(it.solarVoltageMv);

      const charging =
        it.solarCharging === true || it.solarCharging === '1' || it.solarCharging === 1
          ? true
          : it.solarCharging === false || it.solarCharging === '0' || it.solarCharging === 0
          ? false
          : null;

      let powerW: number | null = Number.isFinite(pMw) ? pMw / 1000 : null;
      const currentA: number | null = Number.isFinite(iMa) ? iMa / 1000 : null;
      const voltageV: number | null = Number.isFinite(vMv) ? vMv / 1000 : null;

      if (powerW === null && voltageV !== null && currentA !== null) {
        powerW = voltageV * currentA;
      }

      if (powerW === null && currentA === null && voltageV === null && charging === null) continue;

      outTel.push({ date: d, powerW, currentA, voltageV, charging, valid: valid ?? true });
    }

    if (outTel.length) {
      const bySec = new Map<number, SolarPoint>();
      for (const s of outTel) {
        const sec = Math.floor(s.date.getTime() / 1000);
        bySec.set(sec, s);
      }
      return Array.from(bySec.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
    }

    // B) FALLBACK legacy camera.solarLevels
    const raw = ((this.camera as any)?.solarLevels ?? []) as any[];
    const outLegacy: SolarPoint[] = [];

    for (const it of raw) {
      if (!it || it.date == null) continue;

      const d = this.safeDate(it.date);
      if (!d) continue;

      const pMw = Number(it.powerMw);
      const iMa = Number(it.currentMa);
      const vMv = Number(it.voltageMv);

      const charging =
        it.charging === true || it.charging === '1' || it.charging === 1
          ? true
          : it.charging === false || it.charging === '0' || it.charging === 0
          ? false
          : null;

      const valid =
        it.valid === true || it.valid === '1' || it.valid === 1
          ? true
          : it.valid === false || it.valid === '0' || it.valid === 0
          ? false
          : null;

      if (valid === false) continue;

      let powerW: number | null = Number.isFinite(pMw) ? pMw / 1000 : null;
      const currentA: number | null = Number.isFinite(iMa) ? iMa / 1000 : null;
      const voltageV: number | null = Number.isFinite(vMv) ? vMv / 1000 : null;

      if (powerW === null && voltageV !== null && currentA !== null) {
        powerW = voltageV * currentA;
      }

      if (powerW === null && currentA === null && voltageV === null && charging === null) continue;

      outLegacy.push({ date: d, powerW, currentA, voltageV, charging, valid: valid ?? true });
    }

    const bySec = new Map<number, SolarPoint>();
    for (const s of outLegacy) {
      const sec = Math.floor(s.date.getTime() / 1000);
      bySec.set(sec, s);
    }

    return Array.from(bySec.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  private get showKpis(): boolean {
    return this.plotlySolarData.length > 0 || this.hasExternalBattery;
  }

  private get solarLast(): SolarPoint | null {
    const arr = this.solarLevelsNormalized;
    if (!arr.length) return null;
    return arr[arr.length - 1];
  }

  private get solarPowerW(): number | null {
    return this.solarLast?.powerW ?? null;
  }
  private get solarCurrentA(): number | null {
    return this.solarLast?.currentA ?? null;
  }
  private get solarVoltageV(): number | null {
    return this.solarLast?.voltageV ?? null;
  }
  private get solarCharging(): boolean | null {
    return this.solarLast?.charging ?? null;
  }

  private get solarLastAtLabel(): string | null {
    const d = this.solarLast?.date;
    if (!d) return null;
    return format(d, this.locale === 'en' ? 'MM/dd/yyyy HH:mm' : 'dd/MM/yyyy HH:mm');
  }

  private solarValueUi(value: number | null): { colorClass: string } {
    if (value === null) return { colorClass: 'text-muted' };
    if (this.solarCharging === true) return { colorClass: 'text-batt-good' };
    return { colorClass: 'text-primary' };
  }

  private get solarPowerUi() {
    return this.solarValueUi(this.solarPowerW);
  }
  private get solarCurrentUi() {
    return this.solarValueUi(this.solarCurrentA);
  }
  private get solarVoltageUi() {
    return this.solarValueUi(this.solarVoltageV);
  }

  // ----------------------------------------------------
  // ✅ Option B : Lat/Lon + soleil (midi solaire, elevation, azimut)
  // ----------------------------------------------------

  private parseDmsMinutes(str: string | null | undefined): number | null {
    // Ex: "49d 01,9372m N"  or "002d 03,9354m E"
    if (!str) return null;
    const s = String(str).trim();
    const m = s.match(/(\d+)\s*d\s*([0-9.,]+)\s*m\s*([NSEW])/i);
    if (!m) return null;

    const deg = Number(m[1]);
    const min = Number(String(m[2]).replace(',', '.'));
    const hemi = m[3].toUpperCase();

    if (!Number.isFinite(deg) || !Number.isFinite(min)) return null;

    let val = deg + min / 60;
    if (hemi === 'S' || hemi === 'W') val = -val;
    return val;
  }

  private get cameraLatLon(): { lat: number; lon: number } | null {
    const sh =
      (this.camera as any)?.shadow?.reported ?? (this.camera as any)?.shadow?.desired ?? (this.camera as any)?.shadow;

    const latS = sh?.Position_Latitude;
    const lonS = sh?.Position_Longitude;

    const lat = this.parseDmsMinutes(latS?.S ?? latS);
    const lon = this.parseDmsMinutes(lonS?.S ?? lonS);

    if (lat == null || lon == null) return null;
    return { lat, lon };
  }

  private deg2rad(d: number) {
    return (d * Math.PI) / 180;
  }
  private rad2deg(r: number) {
    return (r * 180) / Math.PI;
  }

  private dayOfYear(date: Date): number {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    return Math.floor(diff / 86400000);
  }

  private solarPosition(dateLocal: Date, lat: number, lon: number): { elevationDeg: number; azimuthDeg: number } {
    const doy = this.dayOfYear(dateLocal);
    const hour = dateLocal.getHours() + dateLocal.getMinutes() / 60 + dateLocal.getSeconds() / 3600;

    const gamma = ((2 * Math.PI) / 365) * (doy - 1 + (hour - 12) / 24);

    const eqTime =
      229.18 *
      (0.000075 +
        0.001868 * Math.cos(gamma) -
        0.032077 * Math.sin(gamma) -
        0.014615 * Math.cos(2 * gamma) -
        0.040849 * Math.sin(2 * gamma));

    const decl =
      0.006918 -
      0.399912 * Math.cos(gamma) +
      0.070257 * Math.sin(gamma) -
      0.006758 * Math.cos(2 * gamma) +
      0.000907 * Math.sin(2 * gamma) -
      0.002697 * Math.cos(3 * gamma) +
      0.00148 * Math.sin(3 * gamma);

    const tz = -dateLocal.getTimezoneOffset() / 60;

    const timeOffset = eqTime + 4 * lon - 60 * tz;
    const tst = hour * 60 + timeOffset;

    let ha = tst / 4 - 180;
    if (ha < -180) ha += 360;

    const latRad = this.deg2rad(lat);
    const haRad = this.deg2rad(ha);

    const cosZenith = Math.sin(latRad) * Math.sin(decl) + Math.cos(latRad) * Math.cos(decl) * Math.cos(haRad);
    const zenith = Math.acos(Math.min(1, Math.max(-1, cosZenith)));
    const elevation = 90 - this.rad2deg(zenith);

    const sinAz = -(Math.sin(haRad) * Math.cos(decl)) / Math.cos(zenith);
    const cosAz = (Math.sin(decl) - Math.sin(latRad) * Math.cos(zenith)) / (Math.cos(latRad) * Math.sin(zenith));

    let az = this.rad2deg(Math.atan2(sinAz, cosAz));
    az = (az + 360) % 360;

    return { elevationDeg: elevation, azimuthDeg: az };
  }

  private solarNoonLocal(dateLocal: Date, lon: number): Date {
    const doy = this.dayOfYear(dateLocal);
    const gamma = ((2 * Math.PI) / 365) * (doy - 1);

    const eqTime =
      229.18 *
      (0.000075 +
        0.001868 * Math.cos(gamma) -
        0.032077 * Math.sin(gamma) -
        0.014615 * Math.cos(2 * gamma) -
        0.040849 * Math.sin(2 * gamma));

    const tz = -dateLocal.getTimezoneOffset() / 60;
    const timeOffset = eqTime + 4 * lon - 60 * tz;
    const noonMin = 720 - timeOffset;

    const d = new Date(dateLocal);
    d.setHours(0, 0, 0, 0);
    d.setMinutes(Math.round(noonMin));
    return d;
  }

  private filterAroundNoon(points: SolarPoint[], noon: Date, minutesRadius = 90): SolarPoint[] {
    const t0 = noon.getTime() - minutesRadius * 60000;
    const t1 = noon.getTime() + minutesRadius * 60000;
    return points.filter((p) => p?.date && p.date.getTime() >= t0 && p.date.getTime() <= t1);
  }

  private quantile(sorted: number[], q: number): number | null {
    if (!sorted.length) return null;
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    const next = sorted[base + 1];
    if (next === undefined) return sorted[base];
    return sorted[base] + rest * (next - sorted[base]);
  }

  private mad(values: number[]): number | null {
    const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
    const med = this.median(v);
    if (med == null) return null;
    const dev = v.map((x) => Math.abs(x - med)).sort((a, b) => a - b);
    return this.median(dev);
  }

  private classifyCloudinessSolar(pointsForDay: SolarPoint[], lat: number, lon: number): CloudinessResult {
    if (!pointsForDay.length) return { cls: 'NO_DATA', score: null, p50: null, p95: null, cv: null, spike: null };

    const noon = this.solarNoonLocal(pointsForDay[0].date, lon);
    const win = this.filterAroundNoon(pointsForDay, noon, 90);

    const P: number[] = [];
    for (const p of win) {
      if (p.powerW == null || !Number.isFinite(p.powerW) || p.powerW < 0.2) continue;
      P.push(p.powerW);
    }

    if (P.length < 12) return { cls: 'NO_DATA', score: null, p50: null, p95: null, cv: null, spike: null };

    const sorted = [...P].sort((a, b) => a - b);
    const p50 = this.median(sorted);
    const p95 = this.quantile(sorted, 0.95);
    const mad = this.mad(sorted);

    if (p50 == null || p95 == null || mad == null || p50 <= 0) {
      return { cls: 'NO_DATA', score: null, p50, p95, cv: null, spike: null };
    }

    const cv = mad / p50;
    const spike = p95 / p50;

    const cvScore = Math.min(1, Math.max(0, (cv - 0.1) / (0.45 - 0.1)));
    const spikeScore = Math.min(1, Math.max(0, (spike - 1.2) / (2.2 - 1.2)));
    const score = 0.65 * cvScore + 0.35 * spikeScore;

    const isOvercast = p50 < 1.0 && cv < 0.18 && spike < 1.4;

    let cls: CloudClass;
    if (isOvercast) cls = 'OVERCAST';
    else cls = score > 0.65 ? 'CLOUDY' : score > 0.35 ? 'MIXED' : 'CLEAR';

    return { cls, score, p50, p95, cv, spike };
  }

  private peakAzimuthAroundNoon(pointsForDay: SolarPoint[], lat: number, lon: number): number | null {
    if (!pointsForDay.length) return null;

    const noon = this.solarNoonLocal(pointsForDay[0].date, lon);
    const win = this.filterAroundNoon(pointsForDay, noon, 120);

    let best: SolarPoint | null = null;
    let bestP = -Infinity;

    for (const p of win) {
      if (p.powerW == null || !Number.isFinite(p.powerW) || p.powerW < 0.2) continue;
      if (p.powerW > bestP) {
        bestP = p.powerW;
        best = p;
      }
    }

    if (!best) return null;
    return this.solarPosition(best.date, lat, lon).azimuthDeg;
  }

  private angularDiffDeg(a: number, b: number): number {
    const d = ((a - b + 540) % 360) - 180;
    return Math.abs(d);
  }

  private dayKeyFromDate(d: Date): string {
    // clé jour locale: YYYY-MM-DD
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private get solarDailyKpis2(): DailySolarKpi2[] {
    const raw = this.solarLevelsNormalized;
    const loc = this.cameraLatLon;
    if (!raw.length || !loc) return [];

    const byDay = new Map<string, SolarPoint[]>();
    for (const p of raw) {
      const k = this.dayKeyFromDate(p.date);
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k)!.push(p);
    }

    const keys = Array.from(byDay.keys()).sort();
    const out: DailySolarKpi2[] = [];

    for (const k of keys) {
      const pts = byDay.get(k)!.sort((a, b) => a.date.getTime() - b.date.getTime());

      const noon = this.solarNoonLocal(pts[0].date, loc.lon);
      const win = this.filterAroundNoon(pts, noon, 90);

      const vals: number[] = [];
      for (const p of win) {
        if (p.powerW == null || !Number.isFinite(p.powerW) || p.powerW < 0.2) continue;

        const sp = this.solarPosition(p.date, loc.lat, loc.lon);
        if (sp.elevationDeg <= 5) continue;

        const e = Math.sin(this.deg2rad(sp.elevationDeg));
        if (e <= 0.05) continue;

        vals.push(p.powerW / e);
      }

      const yMidNorm = this.median(vals);
      const cloud = this.classifyCloudinessSolar(pts, loc.lat, loc.lon);
      const peakAzimuth = this.peakAzimuthAroundNoon(pts, loc.lat, loc.lon);

      out.push({ dayKey: k, yMidNorm, cloud, peakAzimuth });
    }

    return out;
  }

  private get solarCloudinessToday(): { label: string; sub: string | null; ui: { colorClass: string } } {
    const kpis = this.solarDailyKpis2;
    if (!kpis.length) return { label: '—', sub: 'Lat/Long manquants ou pas de data', ui: { colorClass: 'text-muted' } };

    const today = kpis[kpis.length - 1].cloud;

    const map: Record<CloudClass, { label: string; colorClass: string }> = {
      CLEAR: { label: 'Clair', colorClass: 'text-batt-good' },
      MIXED: { label: 'Mixte', colorClass: 'text-primary' },
      CLOUDY: { label: 'Nuageux', colorClass: 'text-warning' },
      OVERCAST: { label: 'Couvert', colorClass: 'text-warning' },
      NO_DATA: { label: '—', colorClass: 'text-muted' },
    };

    const base = map[today.cls];
    const sub =
      today.cls !== 'NO_DATA' && today.cv != null && today.spike != null
        ? `cv ${(today.cv * 100).toFixed(0)}% · spike ${today.spike.toFixed(2)}`
        : null;

    return { label: base.label, sub, ui: { colorClass: base.colorClass } };
  }

  private get solarYieldStatus(): {
    ratio: number | null;
    ratioPctLabel: string;
    sub: string | null;
    ui: { colorClass: string };
  } {
    const kpis = this.solarDailyKpis2;
    if (kpis.length < 8) {
      return { ratio: null, ratioPctLabel: '—', sub: 'Historique insuffisant', ui: { colorClass: 'text-muted' } };
    }

    const today = kpis[kpis.length - 1];
    if (today.yMidNorm == null) {
      return {
        ratio: null,
        ratioPctLabel: '—',
        sub: 'Pas de production exploitable',
        ui: { colorClass: 'text-muted' },
      };
    }

    const prev7 = kpis.slice(-8, -1);

    const baseCandidates = prev7
      .filter((d) => d.yMidNorm != null)
      .filter((d) => d.cloud.cls === 'CLEAR' || d.cloud.cls === 'MIXED')
      .map((d) => d.yMidNorm as number);

    const fallbackCandidates =
      baseCandidates.length >= 3
        ? baseCandidates
        : prev7
            .filter((d) => d.yMidNorm != null)
            .filter((d) => d.cloud.cls !== 'CLOUDY')
            .map((d) => d.yMidNorm as number);

    const base = this.median(fallbackCandidates);
    if (base == null || base <= 0) {
      return { ratio: null, ratioPctLabel: '—', sub: 'Baseline indisponible', ui: { colorClass: 'text-muted' } };
    }

    const ratio = today.yMidNorm / base;

    const azCandidates = prev7
      .filter((d) => d.cloud.cls === 'CLEAR' || d.cloud.cls === 'MIXED')
      .map((d) => d.peakAzimuth)
      .filter((x): x is number => x != null);

    const azBase = azCandidates.length ? this.median(azCandidates) : null;
    const azShift = azBase != null && today.peakAzimuth != null ? this.angularDiffDeg(today.peakAzimuth, azBase) : null;

    const ratioPctLabel = `${(ratio * 100).toFixed(0)}%`;
    const colorClass = ratio < 0.65 ? 'text-danger' : ratio < 0.85 ? 'text-warning' : 'text-batt-good';

    const cloudTxt =
      today.cloud.cls === 'CLEAR'
        ? 'jour clair'
        : today.cloud.cls === 'MIXED'
        ? 'jour mixte'
        : today.cloud.cls === 'OVERCAST'
        ? 'jour couvert'
        : today.cloud.cls === 'CLOUDY'
        ? 'jour nuageux'
        : 'ciel indéterminé';

    let sub = `${cloudTxt} · base7j · norm soleil`;
    if (azShift != null && azShift >= 35) sub += ` · orientation suspecte (Δaz ${azShift.toFixed(0)}°)`;

    return { ratio, ratioPctLabel, sub, ui: { colorClass } };
  }

  // ----------------------------------------------------
  // ✅ Plotly data
  // ----------------------------------------------------

  get plotlySolarData(): Plotly.Data[] {
    const raw = this.solarLevelsNormalized;
    if (!raw.length) return [];

    const xs = raw.map((z) => z.date);
    const ps = raw.map((z) => (z.powerW == null ? NaN : z.powerW));
    const is = raw.map((z) => (z.currentA == null ? NaN : z.currentA));
    const cs = raw.map((z) => z.charging === true);

    const state = cs.map((c) => (c ? 'En charge' : 'Pas en charge'));

    const traces: Plotly.Data[] = [];

    traces.push({
      x: xs,
      y: ps,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Puissance (W)',
      yaxis: 'y',
      line: { width: 2, shape: 'spline', smoothing: 0.6 },
      marker: { size: 5 },
      customdata: state,
      hovertemplate:
        '<b>Puissance</b>: %{y:.2f} W<br>' +
        '<b>État</b>: %{customdata}<br>' +
        `<span style="opacity:.8">%{x|${this.locale === 'en' ? '%m/%d/%Y %H:%M' : '%d/%m/%Y %H:%M'}}</span>` +
        '<extra></extra>',
    } as any);

    traces.push({
      x: xs,
      y: is,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Courant (A)',
      yaxis: 'y2',
      line: { width: 2, shape: 'spline', smoothing: 0.6, dash: 'dot' },
      marker: { size: 5 },
      customdata: state,
      hovertemplate:
        '<b>Courant</b>: %{y:.3f} A<br>' +
        '<b>État</b>: %{customdata}<br>' +
        `<span style="opacity:.8">%{x|${this.locale === 'en' ? '%m/%d/%Y %H:%M' : '%d/%m/%Y %H:%M'}}</span>` +
        '<extra></extra>',
    } as any);

    const cx: Date[] = [];
    const cy: number[] = [];
    for (let i = 0; i < xs.length; i++) {
      if (!cs[i]) continue;
      if (!Number.isFinite(ps[i])) continue;
      cx.push(xs[i]);
      cy.push(ps[i] as any);
    }

    if (cx.length) {
      traces.push({
        x: cx,
        y: cy,
        type: 'scatter',
        mode: 'markers',
        name: 'En charge',
        yaxis: 'y',
        marker: { size: 11, symbol: 'circle-open' },
        hovertemplate:
          '<b>En charge</b><br>' +
          'Puissance: %{y:.2f} W<br>' +
          `<span style="opacity:.8">%{x|${this.locale === 'en' ? '%m/%d/%Y %H:%M' : '%d/%m/%Y %H:%M'}}</span>` +
          '<extra></extra>',
      } as any);
    }

    return traces;
  }

  get plotlyData(): Plotly.Data[] {
    const internal = this.internalBatteryPoints;
    const external = this.externalBatteryPoints;

    const sortByDate = (x: Date[], y: number[]) => {
      const zipped = x.map((d, idx) => ({ d, v: y[idx] })).sort((a, b) => a.d.getTime() - b.d.getTime());
      return { x: zipped.map((z) => z.d), y: zipped.map((z) => z.v) };
    };

    const bx = internal.map((p) => p.date);
    const by = internal.map((p) => p.level);
    const b = sortByDate(bx, by);

    const ebx = external.map((p) => p.date);
    const eby = external.map((p) => p.level);
    const eb = sortByDate(ebx, eby);

    const tTel = this.telemetryTempPoints;

    let tx: Date[] = [];
    let ty: number[] = [];

    if (tTel.length) {
      tx = tTel.map((p) => p.date);
      ty = tTel.map((p) => p.temp);
    } else {
      const temperatureLevels = ((this.camera as any)?.temperatureLevels ?? []).filter(
        (t: any) => t && t.date != null && t.temperature != null
      );

      for (const it of temperatureLevels) {
        const d = this.safeDate(it.date);
        const v = Number(it.temperature);
        if (!d || !Number.isFinite(v)) continue;
        tx.push(d);
        ty.push(v);
      }
    }

    const t = sortByDate(tx, ty);

    const traces: Plotly.Data[] = [];

    if (b.x.length > 0) {
      traces.push({
        x: b.x,
        y: b.y,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Batterie',
        yaxis: 'y',
        line: { width: 3, shape: 'spline', smoothing: 0.6 },
        marker: { size: 6 },
        fill: 'tozeroy',
        fillcolor: 'rgba(95, 201, 228, 0.12)',
        hovertemplate:
          '<b>Batterie</b>: %{y:.0f}%<br>' + '<span style="opacity:.8">%{x|%d/%m/%Y %H:%M}</span>' + '<extra></extra>',
      } as any);
    }

    if (eb.x.length > 0) {
      traces.push({
        x: eb.x,
        y: eb.y,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Batterie externe',
        yaxis: 'y',
        line: { width: 2, shape: 'spline', smoothing: 0.6, dash: 'dash' },
        marker: { size: 6 },
        hovertemplate:
          '<b>Batterie Externe</b>: %{y:.0f}%<br>' +
          '<span style="opacity:.8">%{x|%d/%m/%Y %H:%M}</span>' +
          '<extra></extra>',
      } as any);
    }

    const tempAllZero = t.y.length > 0 && t.y.every((v) => Number(v) === 0);

    if (t.x.length > 0 && !tempAllZero) {
      traces.push({
        x: t.x,
        y: t.y,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Température',
        yaxis: 'y2',
        line: { width: 2, shape: 'spline', smoothing: 0.6, dash: 'dot' },
        marker: { size: 6 },
        hovertemplate:
          '<b>Température</b>: %{y:.1f}°C<br>' +
          '<span style="opacity:.8">%{x|%d/%m/%Y %H:%M}</span>' +
          '<extra></extra>',
      } as any);
    }

    return traces;
  }

  // ----------------------------------------------------
  // Render Plotly
  // ----------------------------------------------------

  private updateSolarGraph(): void {
    const chartDiv = this.$refs.chartSolar as HTMLDivElement | undefined;
    if (!chartDiv) return;

    const traces = this.plotlySolarData as any[];
    if (!traces.length) {
      Plotly.purge(chartDiv);
      return;
    }

    const layout: Partial<Plotly.Layout> = {
      uirevision: `camera-${(this.camera as any)?.id ?? 'unknown'}`,
      autosize: true,

      font: {
        family: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
        color: '#5fc9e4',
        size: 12,
      },

      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      margin: { l: 55, r: 70, b: 60, t: 10, pad: 8 },

      hovermode: 'x unified',
      hoverlabel: {
        bgcolor: 'rgba(10, 10, 10, 0.85)',
        bordercolor: 'rgba(95,201,228,0.35)',
        font: { color: '#e8f7ff', size: 12 },
        align: 'left',
        namelength: -1,
      },

      legend: {
        orientation: 'h',
        x: -0.02,
        xanchor: 'left',
        y: 1.02,
        yanchor: 'bottom',
        font: { color: '#5fc9e4' },
        bgcolor: 'rgba(0,0,0,0)',
        borderwidth: 0,
        itemclick: 'toggle',
        itemdoubleclick: 'toggleothers',
      },

      xaxis: {
        type: 'date',
        tickformat: '%d/%m/%y',
        showgrid: true,
        gridcolor: 'rgba(68,71,90,0.6)',
        showline: true,
        linecolor: 'rgba(95,201,228,0.35)',
        hoverformat: this.locale === 'en' ? '%m/%d/%Y %H:%M' : '%d/%m/%Y %H:%M',
        rangeselector: {
          x: 1,
          xanchor: 'right',
          y: 1.1,
          yanchor: 'top',
          bgcolor: 'rgba(0,0,0,0)',
          font: { color: '#5fc9e4', size: 12 },
          activecolor: 'rgba(95,201,228,0.25)',
          bordercolor: 'rgba(95,201,228,0.35)',
          borderwidth: 1,
          buttons: [
            { count: 1, label: '1J', step: 'day', stepmode: 'backward' },
            { count: 7, label: '7J', step: 'day', stepmode: 'backward' },
            { count: 1, label: '1M', step: 'month', stepmode: 'backward' },
            { step: 'all', label: 'Tout' },
          ],
        },

        ...(this.sharedXRange
          ? { range: this.sharedXRange, autorange: false }
          : { range: this.getDefaultRange(), autorange: false }),
      },

      yaxis: {
        title: { text: 'Puissance (W)', font: { color: '#5fc9e4' } },
        tickfont: { color: '#5fc9e4' },
        showgrid: true,
        gridcolor: 'rgba(68,71,90,0.6)',
        showline: true,
        linecolor: 'rgba(95,201,228,0.35)',
      },

      yaxis2: {
        title: { text: 'Courant (A)', font: { color: '#5fc9e4' }, standoff: 18 },
        tickfont: { color: '#5fc9e4' },
        overlaying: 'y',
        side: 'right',
        showgrid: false,
        showline: true,
        linecolor: 'rgba(95,201,228,0.35)',
      } as any,
    };

    const config: Partial<Plotly.Config> = {
      responsive: true,
      scrollZoom: true,
      displaylogo: false,
      displayModeBar: 'hover',
      modeBarButtonsToRemove: [
        'select2d',
        'lasso2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    };

    Plotly.react(chartDiv, traces, layout, config);
  }

  private updatePlotlyGraph(): void {
    const chartDiv = this.$refs.chart as HTMLDivElement | undefined;
    if (!chartDiv) return;

    const traces = this.plotlyData as any[];
    if (traces.length === 0) {
      Plotly.purge(chartDiv);
      return;
    }

    const hasTemperature = traces.some((tr) => tr?.name === 'Température');
    const rightMargin = 70;

    const layout: Partial<Plotly.Layout> = {
      uirevision: `camera-${(this.camera as any)?.id ?? 'unknown'}`,
      autosize: true,

      font: {
        family: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
        color: '#5fc9e4',
        size: 12,
      },

      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',

      margin: { l: 55, r: rightMargin, b: 80, t: 10, pad: 8 },

      hovermode: 'x unified',
      hoverlabel: {
        bgcolor: 'rgba(10, 10, 10, 0.85)',
        bordercolor: 'rgba(95,201,228,0.35)',
        font: { color: '#e8f7ff', size: 12 },
        align: 'left',
        namelength: -1,
      },

      legend: {
        orientation: 'h',
        x: 0,
        xanchor: 'left',
        y: 1.02,
        yanchor: 'bottom',
        font: { color: '#5fc9e4' },
        bgcolor: 'rgba(0,0,0,0)',
        borderwidth: 0,
        itemclick: 'toggle',
        itemdoubleclick: 'toggleothers',
      },

      xaxis: {
        type: 'date',
        title: { text: 'Date', font: { color: '#5fc9e4' } },
        tickfont: { color: '#5fc9e4' },
        tickformat: '%d/%m/%y',
        automargin: true,

        showgrid: true,
        gridcolor: 'rgba(68,71,90,0.6)',
        gridwidth: 1,
        zeroline: false,

        showline: true,
        linecolor: 'rgba(95,201,228,0.35)',

        showspikes: true,
        spikemode: 'across',
        spikesnap: 'cursor',
        spikethickness: 1,
        spikecolor: 'rgba(95,201,228,0.35)',
        hoverformat: this.locale === 'en' ? '%m/%d/%Y %H:%M' : '%d/%m/%Y %H:%M',

        rangeselector: {
          x: 1,
          xanchor: 'right',
          y: 1.1,
          yanchor: 'top',
          bgcolor: 'rgba(0,0,0,0)',
          font: { color: '#5fc9e4', size: 12 },
          activecolor: 'rgba(95,201,228,0.25)',
          bordercolor: 'rgba(95,201,228,0.35)',
          borderwidth: 1,
          buttons: [
            { count: 1, label: '1J', step: 'day', stepmode: 'backward' },
            { count: 7, label: '7J', step: 'day', stepmode: 'backward' },
            { count: 1, label: '1M', step: 'month', stepmode: 'backward' },
            { step: 'all', label: 'Tout' },
          ],
        },
        ...(this.sharedXRange
          ? { range: this.sharedXRange, autorange: false }
          : { range: this.getDefaultRange(), autorange: false }),
      },

      yaxis: {
        title: { text: 'Batterie (%)', font: { color: '#5fc9e4' } },
        tickfont: { color: '#5fc9e4' },
        range: [0, 100],
        automargin: true,

        showgrid: true,
        gridcolor: 'rgba(68,71,90,0.6)',
        gridwidth: 1,
        zeroline: false,

        showline: true,
        linecolor: 'rgba(95,201,228,0.35)',

        showspikes: true,
        spikemode: 'across',
        spikesnap: 'cursor',
        spikethickness: 1,
        spikecolor: 'rgba(95,201,228,0.25)',
      },

      ...(hasTemperature
        ? {
            yaxis2: {
              title: {
                text: 'Température (°C)',
                font: { color: '#5fc9e4' },
                standoff: 18,
              },
              tickfont: { color: '#5fc9e4' },
              automargin: true,

              overlaying: 'y',
              side: 'right',
              showgrid: false,

              showline: true,
              linecolor: 'rgba(95,201,228,0.35)',
            } as any,
          }
        : {}),
    };

    const config: Partial<Plotly.Config> = {
      responsive: true,
      scrollZoom: true,
      displaylogo: false,
      displayModeBar: 'hover',
      modeBarButtonsToRemove: [
        'select2d',
        'lasso2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
      toImageButtonOptions: {
        format: 'png',
        filename: `battery_temperature_${(this.camera as any)?.id ?? 'camera'}`,
        scale: 2,
      },
    };

    Plotly.react(chartDiv, traces, layout, config);
  }

  // ----------------------------------------------------
  // X-axis sync
  // ----------------------------------------------------

  private attachXAxisSync(): void {
    const top = this.$refs.chart as any;
    const bot = this.$refs.chartSolar as any;
    if (!top || !bot) return;

    const extractRange = (e: any): [any, any] | null => {
      const r = e?.['xaxis.range'];
      const r0 = e?.['xaxis.range[0]'] ?? (Array.isArray(r) ? r[0] : null);
      const r1 = e?.['xaxis.range[1]'] ?? (Array.isArray(r) ? r[1] : null);
      if (r0 == null || r1 == null) return null;
      return [r0, r1];
    };

    const lock = (ms = 200) => {
      this.syncingX = true;
      if (this.syncingXTimeout != null) window.clearTimeout(this.syncingXTimeout);
      this.syncingXTimeout = window.setTimeout(() => {
        this.syncingX = false;
        this.syncingXTimeout = null;
      }, ms);
    };

    const sync = (target: any, e: any) => {
      if (this.syncingX) return;

      const autorange = e?.['xaxis.autorange'];
      const range = extractRange(e);

      if (autorange == null && range == null) return;

      lock(250);

      if (autorange) {
        this.sharedXRange = null;
        Plotly.relayout(target, { 'xaxis.autorange': true })
          .then(() => lock(150))
          .catch(() => lock(150));
      } else if (range) {
        this.sharedXRange = range;
        Plotly.relayout(target, { 'xaxis.range': range, 'xaxis.autorange': false })
          .then(() => lock(150))
          .catch(() => lock(150));
      }
    };

    top.removeAllListeners?.('plotly_relayout');
    bot.removeAllListeners?.('plotly_relayout');

    top.on('plotly_relayout', (e: any) => sync(bot, e));
    bot.on('plotly_relayout', (e: any) => sync(top, e));
  }

  // ----------------------------------------------------
  // Lifecycle
  // ----------------------------------------------------

  private readonly DEBUG_PLOTLY = true;
  private log(...args: any[]): void {
    if (this.DEBUG_PLOTLY) console.log('[BatterieLevelChart]', ...args);
  }

  private scheduleUpdateAll(reason: string): void {
    this.log('scheduleUpdateAll:', reason);
    this.$nextTick(() => {
      this.updatePlotlyGraph();
      this.updateSolarGraph();
    });
  }

  public resizeBatteryGraph(): void {
    const chartDiv = this.$refs.chart as HTMLDivElement;
    if (chartDiv && this.plotlyData.length > 0) {
      Plotly.Plots.resize(chartDiv);
    }
  }

  public mounted(): void {
    this.scheduleUpdateAll('mounted');
    this.$nextTick(() => this.attachXAxisSync());
  }

  public beforeDestroy(): void {
    const top = this.$refs.chart as any;
    const bot = this.$refs.chartSolar as any;
    top?.removeAllListeners?.('plotly_relayout');
    bot?.removeAllListeners?.('plotly_relayout');

    if (this.syncingXTimeout != null) {
      window.clearTimeout(this.syncingXTimeout);
      this.syncingXTimeout = null;
    }
  }

  // ----------------------------------------------------
  // Watches
  // ----------------------------------------------------

  @Watch('camera.batteryLevels', { immediate: true, deep: true })
  public onBatteryLevelsChange(): void {
    this.scheduleUpdateAll('batteryLevels changed');
  }

  @Watch('camera.solarLevels', { immediate: true, deep: true })
  public onSolarLevelsChange(): void {
    this.scheduleUpdateAll('solarLevels changed');
  }

  @Watch('camera.externalBatteryLevels', { immediate: true, deep: true })
  public onExternalBatteryLevelsChange(): void {
    this.scheduleUpdateAll('externalBatteryLevels changed');
  }

  @Watch('camera.temperatureLevels', { immediate: true, deep: true })
  public onTemperatureLevelsChange(): void {
    this.scheduleUpdateAll('temperatureLevels changed');
  }

  @Watch('telemetry', { immediate: true, deep: true })
  public onTelemetryChange(): void {
    this.scheduleUpdateAll('telemetry changed');
  }
}
